import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, RefreshCw, Save, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useTenantStore } from '@/hooks/useTenantStore';

export default function PublicCompsSettings() {
  const { tenantId } = useTenantStore();
  const [base, setBase] = useState('USD');
  const [csvUrl, setCsvUrl] = useState('');
  const [newsEnabled, setNewsEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!tenantId) return;

    const { data } = await supabase
      .from('settings')
      .select('pc_base_currency, pc_news_enabled')
      .eq('company_id', tenantId)
      .maybeSingle();

    if (data) {
      setBase(data.pc_base_currency || 'USD');
      setNewsEnabled(!!data.pc_news_enabled);
    }
  }

  async function save() {
    if (!tenantId) {
      toast.error('No company selected');
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase.from('settings').upsert(
        {
          company_id: tenantId,
          pc_base_currency: base,
          pc_news_enabled: newsEnabled,
        },
        {
          onConflict: 'company_id',
        }
      );

      if (error) throw error;
      toast.success('Public comps settings saved');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function seedOpenCsv() {
    if (!csvUrl.trim()) {
      toast.error('Please paste a CSV URL');
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase.functions.invoke('pc-seed-open-dataset', {
        body: { csvUrl },
      });

      if (error) throw error;
      toast.success('Open dataset imported successfully');
      setCsvUrl('');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function rebuildCache() {
    setBusy(true);
    try {
      const { error } = await supabase.functions.invoke('pc-rebuild-cache', {
        body: {},
      });

      if (error) throw error;
      toast.success('Public comps cache rebuilt');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (tenantId) {
      load();
    }
  }, [tenantId]);

  if (!tenantId) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-6">
          <p className="text-muted-foreground">
            Please select a company to manage public comps settings.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Public Comps (OpenAI + Public Data)</CardTitle>
        <Button variant="outline" size="sm" onClick={rebuildCache} disabled={busy}>
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-1" />
          )}
          Rebuild Cache
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="base-currency">Base Currency</Label>
            <Select value={base} onValueChange={setBase}>
              <SelectTrigger id="base-currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="ZAR">ZAR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="AED">AED</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="news-enabled">News Extraction</Label>
            <div className="flex items-center gap-2 h-10">
              <Switch id="news-enabled" checked={newsEnabled} onCheckedChange={setNewsEnabled} />
              <span className="text-sm text-muted-foreground">
                {newsEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          <div className="flex items-end">
            <Button onClick={save} disabled={busy} className="w-full">
              <Save className="h-4 w-4 mr-1" />
              Save Settings
            </Button>
          </div>
        </div>

        <div className="border border-border rounded-lg p-4 space-y-3">
          <div>
            <Label htmlFor="csv-url" className="text-sm font-medium">
              Seed from Open Dataset (CSV)
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Paste an open dataset URL (e.g., Kaggle hosted CSV) to seed historical comps.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Input
              id="csv-url"
              placeholder="https://example.com/dataset.csv"
              value={csvUrl}
              onChange={(e) => setCsvUrl(e.target.value)}
              disabled={busy}
            />
            <Button onClick={seedOpenCsv} disabled={busy || !csvUrl.trim()} variant="secondary">
              <Upload className="h-4 w-4 mr-1" />
              Import CSV
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
